/**
 * References Section - Unified Citations
 *
 * Consolidates all academic and industry sources cited throughout the homepage
 * Background: Clean light section for professional credibility
 */

const ReferencesSection = () => {
  return (
    <section className="relative bg-white/90 py-12 sm:py-16 lg:py-24 overflow-hidden shadow-2xl">
      {/* CONTENT CONTAINER */}
      <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6">
        {/* SECTION HEADER */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 leading-tight mb-4">
            References & Sources
          </h2>
          <p className="text-lg text-slate-600 max-w-[700px] mx-auto">
            All claims and statistics are backed by industry research and
            academic studies
          </p>
        </div>

        {/* CITATIONS GRID */}
        <div className="bg-white/90 rounded-2xl p-5 sm:p-8 lg:p-10 shadow-xl border-2 border-slate-200">
          <div className="space-y-6 text-sm text-slate-700">
            {/* Research Section Citations */}
            <div>
              <h3 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">
                The Profitability Paradox Research
              </h3>
              <div className="space-y-4 pl-4 border-l-2 border-[#B87333]">
                <p id="citation-1" className="leading-relaxed">
                  <span className="font-semibold text-[#B87333]">[1]</span> PwC.
                  (2025).
                  <em>
                    {" "}
                    Asset and Wealth Management Revolution 2025 – The
                    Profitability Paradox: Competing for Relevance and Returns.
                  </em>{" "}
                  Retrieved from{" "}
                  <a
                    href="https://www.pwc.com/gx/en/issues/transformation/asset-and-wealth-management-revolution.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#B87333] hover:underline font-medium"
                  >
                    PwC Global
                  </a>
                </p>
                <p id="citation-2" className="leading-relaxed">
                  <span className="font-semibold text-[#B87333]">[2]</span>{" "}
                  Rithum. (2025).
                  <em>
                    {" "}
                    The Profitability Paradox: Ecommerce Growth Challenges in
                    2025.
                  </em>{" "}
                  Walmart Commerce Technologies Webinar, July 2025.
                </p>
                <p id="citation-3" className="leading-relaxed">
                  <span className="font-semibold text-[#B87333]">[3]</span>{" "}
                  McKinsey & Company. (various years).
                  <em>
                    {" "}
                    Published research on business process automation and
                    operational efficiency.
                  </em>
                </p>
                <p id="citation-4" className="leading-relaxed">
                  <span className="font-semibold text-[#B87333]">[4]</span>{" "}
                  World Economic Forum. (2026).
                  <em>
                    {" "}
                    Four Futures for Jobs in the New Economy: AI and Talent in
                    2030.
                  </em>{" "}
                  White Paper, January 2026. Retrieved from{" "}
                  <a
                    href="https://www.weforum.org/publications/four-futures-for-jobs-in-the-new-economy-ai-and-talent-in-2030/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#B87333] hover:underline font-medium"
                  >
                    World Economic Forum
                  </a>
                </p>
              </div>
            </div>

            {/* Solution Section Citations */}
            <div>
              <h3 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3 pt-6 border-t border-slate-200">
                Solution Effectiveness Studies
              </h3>
              <div className="space-y-4 pl-4 border-l-2 border-[#B87333]">
                <p id="sol-citation-1" className="leading-relaxed">
                  <span className="font-semibold text-[#B87333]">[5]</span> RST
                  Software. (2023).
                  <em>
                    {" "}
                    Business Process Reengineering – Increasing Profits through
                    Efficiency.
                  </em>{" "}
                  Case studies on BPR implementation and results demonstrating
                  personnel cost reduction, financial expense optimization, and
                  cycle time improvements.
                </p>
                <p id="sol-citation-2" className="leading-relaxed">
                  <span className="font-semibold text-[#B87333]">[6]</span>{" "}
                  McKinsey & Company. (2016).
                  <em>
                    {" "}
                    The next acronym you need to know about: RPA (Robotic
                    Process Automation).
                  </em>{" "}
                  Interview with Leslie Willcocks on robotic process automation
                  ROI, productivity gains, and cost savings on automated
                  activities.
                </p>
              </div>
            </div>

            {/* Additional Industry Research */}
            <div>
              <h3 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3 pt-6 border-t border-slate-200">
                Supporting Industry Research
              </h3>
              <div className="space-y-4 pl-4 border-l-2 border-slate-400/30">
                <p className="leading-relaxed text-slate-600">
                  Additional research from industry leaders including Deloitte,
                  Gartner, and Harvard Business Review supports the frameworks
                  and methodologies referenced throughout this analysis.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ACADEMIC INTEGRITY NOTE */}
        <div className="mt-8 text-center">
          <p className="text-xs text-slate-500 italic">
            All statistics and claims are independently verifiable through the
            cited sources. Updated regularly to reflect the latest industry
            research.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ReferencesSection;
